import { invalidateConfigQueries } from "@/lib/config-pagination";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { previewDraft, saveDraft, type DraftOperation } from "@/api/management";
import {
  MANAGEMENT_SAVE_KEY,
  ManagementDraftContext,
  type ManagementDraftValue,
} from "@/components/agents/configuration-server/stages/managementDraftContext";
import { stageKeys } from "@/hooks/useConfigServerStages";
import { managementKeys } from "@/hooks/useManagementLists";
import { LIST_QUERY_OPTIONS } from "@/lib/config-pagination";
import { verdictErrorMessage } from "@/lib/config-server-messages";
import {
  compactOperations,
  draftFromPreview,
  EMPTY_DRAFT,
} from "@/lib/management-draft";

/**
 * Rascunho do Gerenciamento (15/10): aprovar, tirar do escopo e voltar para
 * pendente mudam só a tela; o "Salvar" grava tudo numa chamada. As três abas
 * dividem o mesmo rascunho, que sobrevive à troca de aba.
 */
export function ManagementDraftProvider({
  projectId,
  artifactId,
  children,
}: {
  projectId: string;
  /** Sem extração pronta não há o que decidir. */
  artifactId: string | null;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const artifact = artifactId ?? undefined;
  const [operations, setOperations] = useState<DraftOperation[]>([]);
  const compact = useMemo(() => compactOperations(operations), [operations]);
  const previewRevision = useMemo(() => JSON.stringify(compact), [compact]);

  const fetchPreview = useCallback(
    (ops: DraftOperation[]) =>
      previewDraft(projectId, ops, artifact).then((data) => ({
        data,
        count: ops.length,
      })),
    [projectId, artifact],
  );
  const preview = useQuery({
    queryKey: managementKeys.preview(projectId, artifact, compact),
    queryFn: () => fetchPreview(compact),
    enabled: !!artifactId && compact.length > 0,
    placeholderData: keepPreviousData,
    ...LIST_QUERY_OPTIONS,
  });
  const previewCurrent =
    compact.length === 0 ||
    (!preview.isPlaceholderData && preview.data?.count === compact.length);
  const state = useMemo(() => {
    if (compact.length === 0) return EMPTY_DRAFT;
    const counted = preview.data?.count ?? 0;
    return draftFromPreview(preview.data?.data, compact.slice(counted));
  }, [compact, preview.data]);

  const mutation = useMutation({
    mutationKey: MANAGEMENT_SAVE_KEY(projectId),
    mutationFn: (batch: DraftOperation[]) =>
      saveDraft(projectId, batch, artifact),
  });

  const stage = useCallback(
    (operation: DraftOperation) =>
      setOperations((current) => [...current, operation]),
    [],
  );
  const discard = useCallback(() => setOperations([]), []);
  const previewWith = useCallback(
    async (operation: DraftOperation) => {
      const next = compactOperations([...operations, operation]);
      const result = await queryClient.fetchQuery({
        queryKey: managementKeys.preview(projectId, artifact, next),
        queryFn: () => fetchPreview(next),
      });
      return result.data;
    },
    [operations, queryClient, projectId, artifact, fetchPreview],
  );
  const save = useCallback(async () => {
    if (compact.length === 0) return true;
    const changes = state.changes;
    try {
      await mutation.mutateAsync(compact);
      setOperations([]);
      toast.success(t("{{count}} changes saved", { count: changes }));
      const keys: QueryKey[] = [
        stageKeys.all(projectId),
        ["config-server", "org-structure", projectId],
        ["config-server", "sscui-tree", projectId],
        ["sscui-tree-activities", projectId],
        ["migration-items", projectId],
        ["pipeline", projectId],
      ];
      await Promise.allSettled(
        keys.map((key) => invalidateConfigQueries(queryClient, key)),
      );
      return true;
    } catch (error) {
      toast.error(verdictErrorMessage(error, t));
      return false;
    }
  }, [compact, state.changes, mutation, projectId, queryClient, t]);

  const value = useMemo<ManagementDraftValue>(
    () => ({
      ...state,
      details: { projectId, artifactId: artifact, operations: compact },
      previewRevision,
      previewCurrent,
      pendingOperations: compact.length,
      stage,
      previewWith,
      discard,
      save,
      saving: mutation.isPending,
    }),
    [
      state,
      projectId,
      artifact,
      compact,
      previewRevision,
      previewCurrent,
      stage,
      previewWith,
      discard,
      save,
      mutation.isPending,
    ],
  );
  return (
    <ManagementDraftContext.Provider value={value}>
      {children}
    </ManagementDraftContext.Provider>
  );
}
